import { useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { FilterDrawer, RhfSelect } from '@core/components';
import { useFilterDrawer } from '@core/hooks/useFilterDrawer';
import { getOptions } from '@core/utils/enum';
import type { DesignFilters } from '../../api/design';
import { VestmentCategory } from '../enum/vestmentCategory';
import { EMPTY_DESIGN_FILTERS } from '../utils/design';
import { DESIGN_SUBTYPE_OPTIONS_BY_CATEGORY } from '../utils/vestment';

interface DesignFilterDrawerProps {
  open: boolean;
  filters: DesignFilters;
  onApply: (filters: DesignFilters) => void;
  onClear: () => void;
  onClose: () => void;
}

const DesignFilterDrawer: React.FC<DesignFilterDrawerProps> = ({
  open,
  filters,
  onApply,
  onClear,
  onClose,
}) => {
  const { methods, handleApply, handleClear } = useFilterDrawer({
    open,
    filters,
    emptyFilters: EMPTY_DESIGN_FILTERS,
    onApply,
    onClear,
  });
  const { watch, setValue } = methods;

  const category = watch('category');

  const categoryOptions = useMemo(
    () => [
      { value: '', label: 'All categories' },
      ...getOptions(VestmentCategory),
    ],
    []
  );

  const hasVariant =
    category !== '' &&
    category !== VestmentCategory.Alb &&
    category !== VestmentCategory.Mitre;

  const variantOptions = useMemo(() => {
    const options = [{ value: '', label: 'All variants' }];
    if (hasVariant) {
      options.push(...DESIGN_SUBTYPE_OPTIONS_BY_CATEGORY[category]);
    }
    return options;
  }, [category, hasVariant]);

  return (
    <FormProvider {...methods}>
      <FilterDrawer
        open={open}
        title="Filter designs"
        onApply={handleApply}
        onClear={handleClear}
        onClose={onClose}
      >
        <RhfSelect<DesignFilters>
          name="category"
          label="Category"
          options={categoryOptions}
          onValueChange={() => setValue('variant', '')}
        />

        <RhfSelect<DesignFilters>
          name="variant"
          label="Variant"
          options={variantOptions}
          disabled={!hasVariant}
        />
      </FilterDrawer>
    </FormProvider>
  );
};

export default DesignFilterDrawer;
